import React, { useState, useCallback, useMemo, Suspense } from "react";
import InstallmentPlan from "../billing/InstallmentPlan";

// Lazy load form components for code splitting
const FeeType = React.lazy(() => import("../billing/FeeType"));
const FeeCycle = React.lazy(() => import("../billing/FeeCycle"));
const FeePlan = React.lazy(() => import("../billing/FeePlan"));
const AssignFeePlan = React.lazy(() => import("../billing/AssignFeePlan"));
// const FeePlanAssignment = React.lazy(() =>
//   import("../billing/FeePlanAssignment")
// );
const FeePlanClassWise = React.lazy(() =>
  import("../billing/FeePlanClassWise")
);

const FeeSetupForms = React.memo(() => {
  const [activeTab, setActiveTab] = useState("Fee Type");

  // Memoize the tab change handler to prevent unnecessary re-renders
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  // Memoize the tab content to avoid re-computing
  const tabContent = useMemo(() => {
    switch (activeTab) {
      case "Fee Type":
        return <FeeType />;
      case "Fee Cycle":
        return <FeeCycle />;
      case "Fee Plan":
        return <FeePlan />;
      case "Installment Plan":
        return <InstallmentPlan />;
      case "Assign Fee Plan":
        return <AssignFeePlan />;
      // case "Fee Plan Assignment":
      //   return <FeePlanAssignment />;
      case "Fee Plan Year Group Wise":
        return <FeePlanClassWise />;
      default:
        return null;
    }
  }, [activeTab]);

  return (
    <div className="">
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Fee Setup</h2>
      </div>
      {/* Bootstrap Nav Tabs */}
      <ul
        className="nav nav-tabs setup-tabs mb-3 gap-1"
        id="billingTabs"
        role="tablist"
      >
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Fee Type" ? "active" : ""}`}
            id="fee-type-tab"
            data-bs-toggle="tab"
            data-bs-target="#fee-type"
            type="button"
            role="tab"
            aria-controls="fee-type"
            aria-selected={activeTab === "Fee Type"}
            onClick={() => handleTabChange("Fee Type")}
          >
            Fee Type
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Fee Cycle" ? "active" : ""}`}
            id="fee-cycle-tab"
            data-bs-toggle="tab"
            data-bs-target="#fee-cycle"
            type="button"
            role="tab"
            aria-controls="fee-cycle"
            aria-selected={activeTab === "Fee Cycle"}
            onClick={() => handleTabChange("Fee Cycle")}
          >
            Fee Cycle
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === "Fee Plan" ? "active" : ""}`}
            id="fee-plan-tab"
            data-bs-toggle="tab"
            data-bs-target="#fee-plan"
            type="button"
            role="tab"
            aria-controls="fee-plan"
            aria-selected={activeTab === "Fee Plan"}
            onClick={() => handleTabChange("Fee Plan")}
          >
            Fee Plan
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${
              activeTab === "Installment Plan" ? "active" : ""
            }`}
            id="installment-plan-tab"
            data-bs-toggle="tab"
            data-bs-target="#installment-plan"
            type="button"
            role="tab"
            aria-controls="installment-plan"
            aria-selected={activeTab === "Installment Plan"}
            onClick={() => handleTabChange("Installment Plan")}
          >
            Installment Plan
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${
              activeTab === "Assign Fee Plan" ? "active" : ""
            }`}
            id="assign-fee-plan-tab"
            data-bs-toggle="tab"
            data-bs-target="#assign-fee-plan"
            type="button"
            role="tab"
            aria-controls="assign-fee-plan"
            aria-selected={activeTab === "Assign Fee Plan"}
            onClick={() => handleTabChange("Assign Fee Plan")}
          >
            Assign Fee Plan
          </button>
        </li>
        {/* <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${
              activeTab === "Fee Plan Year Group Wise" ? "active" : ""
            }`}
            id="fee-plan-year-group-wise-tab"
            data-bs-toggle="tab"
            data-bs-target="#fee-plan-year-group-wise"
            type="button"
            role="tab"
            aria-controls="fee-plan-year-group-wise"
            aria-selected={activeTab === "Fee Plan Year Group Wise"}
            onClick={() => handleTabChange("Fee Plan Year Group Wise")}
          >
            Fee Plan Year Group Wise
          </button>
        </li> */}
      </ul>

      {/* Tab Content with Suspense */}
      <div className="tab-content" id="billingTabContent">
        <Suspense fallback={<div className="p-3">Loading...</div>}>
          <div
            className={`tab-pane fade ${
              activeTab === "Fee Type" ? "show active" : ""
            }`}
            id="fee-type"
            role="tabpanel"
            aria-labelledby="fee-type-tab"
          >
            {activeTab === "Fee Type" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Fee Cycle" ? "show active" : ""
            }`}
            id="fee-cycle"
            role="tabpanel"
            aria-labelledby="fee-cycle-tab"
          >
            {activeTab === "Fee Cycle" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Fee Plan" ? "show active" : ""
            }`}
            id="fee-plan"
            role="tabpanel"
            aria-labelledby="fee-plan-tab"
          >
            {activeTab === "Fee Plan" && tabContent}
          </div>
          {/* Add the missing Installment Plan tab pane */}
          <div
            className={`tab-pane fade ${
              activeTab === "Installment Plan" ? "show active" : ""
            }`}
            id="installment-plan"
            role="tabpanel"
            aria-labelledby="installment-plan-tab"
          >
            {activeTab === "Installment Plan" && tabContent}
          </div>
          <div
            className={`tab-pane fade ${
              activeTab === "Assign Fee Plan" ? "show active" : ""
            }`}
            id="assign-fee-plan"
            role="tabpanel"
            aria-labelledby="assign-fee-plan-tab"
          >
            {activeTab === "Assign Fee Plan" && tabContent}
          </div>
          {/* <div
            className={`tab-pane fade ${
              activeTab === "Fee Plan Year Group Wise" ? "show active" : ""
            }`}
            id="fee-plan-year-group-wise"
            role="tabpanel"
            aria-labelledby="fee-plan-year-group-wise-tab"
          >
            {activeTab === "Fee Plan Year Group Wise" && tabContent}
          </div> */}
        </Suspense>
      </div>
    </div>
  );
});

export default FeeSetupForms;
